const fileSafetyContent = {
  title: "Undo, Preview & File Safety",
  description: "How Sortio protects your files: preview before apply, dry runs, full undo history, no overwrites, and protected system folders.",
  sections: [
    {
      id: "overview",
      title: "How Sortio Protects Your Files",
      content: `
        <p class="text-[#1d1d1f] text-lg mb-6">
          Sortio physically moves and renames files, so it is built around one principle:
          <strong>nothing irreversible happens without you seeing it first, and everything it does can be
          undone.</strong> The protection comes in layers:
        </p>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div class="border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-2">Preview before apply</h4>
            <p class="text-[#424245] text-sm">Every AI sort shows the full plan (moves, renames, new folders) before anything happens.</p>
          </div>
          <div class="border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-2">Dry Run for automations</h4>
            <p class="text-[#424245] text-sm">Rules can be simulated against real files with zero changes on disk.</p>
          </div>
          <div class="border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-2">Undo via History</h4>
            <p class="text-[#424245] text-sm">Every applied sort is recorded and reversible, in whole or file by file.</p>
          </div>
          <div class="border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-2">No overwrites, trash not delete</h4>
            <p class="text-[#424245] text-sm">Name collisions get a numbered suffix instead of replacing a file, and the Delete action moves files to the trash.</p>
          </div>
        </div>

        <div class="border border-[#06c]/20 rounded-xl p-5 bg-[#06c]/5 backdrop-blur">
          <h4 class="font-semibold text-[#1d1d1f] mb-2">A note on backups</h4>
          <p class="text-[#424245] text-sm">Before each sort, Sortio writes a zip of the files it is about to move to <code>~/.sortio/backups</code>, and keeps the backups from your 10 most recent sorts. That is a count, not a time limit, so an eleventh sort removes the oldest. Sorts larger than the size limit (500 MB by default, adjustable in Settings) skip the backup and proceed. Undo does not depend on it either way, since it replays the recorded moves in reverse. Keep your regular backup tool (Time Machine, File History, cloud sync) running as usual; Sortio's moves and renames look like ordinary file operations to it.</p>
        </div>
      `
    },
    {
      id: "preview",
      title: "Preview Before Apply",
      content: `
        <p class="text-[#1d1d1f] text-lg mb-6">
          When you run an AI sort, Sortio does not start moving files. It generates a
          <strong>preview</strong> of the plan first:
        </p>

        <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden mb-6">
          <div class="p-6 space-y-3 text-[#424245]">
            <p>1. Which files will be moved, and to where</p>
            <p>2. Which files will be renamed, and their new names</p>
            <p>3. Which new folders will be created</p>
            <p>4. Which files will be left unchanged</p>
          </div>
        </div>

        <p class="text-[#1d1d1f] text-lg mb-6">
          Nothing touches your disk until you click <strong>Apply</strong>. If the preview isn't what you
          wanted, revise your prompt and generate a new one; previews are free, and you can generate as
          many as you like.
        </p>

        <p class="text-[#1d1d1f] text-lg">
          Automations have the same idea in the form of a <strong>Dry Run</strong>: the rule is simulated
          against your real files and you see exactly what would match and what would happen, with zero
          changes made. Details in the <a href="/docs/rules-engine" class="text-[#06c]">Rules Engine</a> docs.
        </p>
      `
    },
    {
      id: "undo",
      title: "Undo and Sort History",
      content: `
        <p class="text-[#1d1d1f] text-lg mb-6">
          Every applied sort is recorded on the <strong>History</strong> page as a session: the folder,
          the prompt you used, every file move with its original and new path, every rename, and every
          folder created. That record is what makes undo possible.
        </p>

        <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden mb-6">
          <div class="p-6 space-y-5">
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Undo a whole session</h4>
              <p class="text-[#424245]">Find the session in History (newest first) and click undo. Sortio moves every file back to its original location, and cleans up folders it created if they're left empty.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Undo individual moves</h4>
              <p class="text-[#424245]">Open a session's details and select just the moves you want reversed. Useful when a sort got most files right and misplaced a few.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Undo background automation runs</h4>
              <p class="text-[#424245]">When a watched folder's automations move files automatically, the notification includes a one-click <strong>Undo</strong> that restores everything from that run. See <a href="/docs/watch-folders-automations" class="text-[#06c]">Watch Folders &amp; Automations</a>.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Iterations are separate sessions</h4>
              <p class="text-[#424245]">If you refine a sort with follow-up prompts, each iteration gets its own History entry, so you can undo just the last step without losing the rest.</p>
            </div>
          </div>
        </div>

        <p class="text-[#1d1d1f] text-lg">
          This is what makes Sortio safe to experiment with: try an aggressive organization scheme, look
          at the result, and undo anything you don't like.
        </p>
      `
    },
    {
      id: "no-overwrites",
      title: "No Overwrites, Ever",
      content: `
        <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden mb-6">
          <div class="p-6 space-y-5">
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Name collisions get a suffix</h4>
              <p class="text-[#424245]">If a move or rename would land on a path where a different file already exists, Sortio appends a number to the incoming file's name instead of replacing the existing file. All move, rename, and archive operations go through this same collision check.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Delete means trash</h4>
              <p class="text-[#424245]">The Delete action in automations moves files to your system trash, not permanent deletion. You can recover them from the trash like any other file.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">System and hidden files are skipped</h4>
              <p class="text-[#424245]">Sorts don't touch dotfiles or system files, so an aggressive prompt can't break an application's working files.</p>
            </div>
          </div>
        </div>
      `
    },
    {
      id: "protected-folders",
      title: "Protected System Folders",
      content: `
        <p class="text-[#1d1d1f] text-lg mb-6">
          As of v8.18.0, Sortio refuses to organize, watch, or move files out of locations where moving
          files could damage your machine:
        </p>

        <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden mb-6">
          <div class="p-6 space-y-3 text-[#424245]">
            <p>• The filesystem root and drive roots (<code>/</code>, <code>C:\\</code>)</p>
            <p>• OS directories and everything inside them (<code>/System</code>, <code>/Library</code>, <code>C:\\Windows\\System32</code>, and similar)</p>
            <p>• Your home folder itself (sorting all of it would scatter settings and app data)</p>
          </div>
        </div>

        <p class="text-[#1d1d1f] text-lg">
          Normal working folders inside your home directory, like <code>~/Downloads</code> or
          <code>~/Documents/Clients</code>, are unaffected. The guard is enforced at every entry point:
          folder selection, sort apply, automation runs, Space creation, the background watcher, and rule
          move destinations.
        </p>
      `
    },
    {
      id: "limits",
      title: "What Undo Can't Do",
      content: `
        <p class="text-[#1d1d1f] text-lg mb-6">
          Undo works by moving files back from where Sortio put them. A few honest limits follow from
          that:
        </p>

        <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden mb-6">
          <div class="p-6 space-y-5">
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">External changes break undo for that file</h4>
              <p class="text-[#424245]">If you move, rename, or delete a file with Finder, Explorer, or another app after Sortio placed it, Sortio can no longer undo that specific move. It will tell you which files couldn't be restored and still reverse the rest.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Undo sessions in reverse order</h4>
              <p class="text-[#424245]">If you've run several sorts on the same folder, undo the newest session first. Undoing out of order can put files in unexpected places.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">History tracks Sortio's own operations</h4>
              <p class="text-[#424245]">Sort History records what Sortio's sorts did. It is not a general-purpose file activity monitor and doesn't track changes you make outside the app.</p>
            </div>
          </div>
        </div>

        <p class="text-[#1d1d1f] text-lg">
          Stuck on an undo, or did something go sideways? Email
          <a href="mailto:marcus@getsortio.com" class="text-[#06c]">marcus@getsortio.com</a> and we'll
          help you put things back.
        </p>
      `
    }
  ]
};

export default fileSafetyContent;
